import { useLazyUser } from "$global-providers/user-provider";
import { gql } from "@apollo/client";
import type { QueryPullEpocusBillingStatusArgs } from "elephant-web/gql";
import { Role } from "elephant-web/gql";
import { useEffect, useMemo, useState } from "react";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import type {
  BillingHoldReason,
  BillingMissingItem,
  BillingState,
  EpocusBillingStatus,
} from "../collections/epocus-billing-status";
import { RXDB_EPOCUS_PROGRAM_COLLECTION_NAME } from "../collections/epocus-program";
import type { RxDBCollections } from "../provider";
import {
  SyncEpocusBillingStatusPullDocument,
  type WholeSyncableEpocusBillingStatusFragment,
} from "./__generated__/epocus-billing-status.gql";
import { createPersistedCheckpointPullBuilders } from "./create-persisted-checkpoint-pull-builders";
import {
  EPOCUS_BILLING_STATUS_PULL_ROLES,
  isReplicationRoleAllowed,
} from "./replication-role-allowlist";
import {
  type ReplicationPullConfig,
  useGqlReplication,
} from "./use-gql-replication";

const PROGRAM_WIDE_ROLES: readonly Role[] = [Role.QaAdmin, Role.ProgramAdmin];

const programSetKey = (programIds: readonly string[]): string => {
  const joined = [...programIds].sort().join(",");
  let hash = 0;
  for (const char of joined) {
    hash = (hash * 31 + char.charCodeAt(0)) | 0;
  }
  return `programs-${(hash >>> 0).toString(36)}`;
};

export const useReplicateEpocusBillingStatus = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<EpocusBillingStatus>;
}) => {
  const user = useLazyUser();
  const role = user?.role;
  const performerId =
    role && PROGRAM_WIDE_ROLES.includes(role) ? null : (user?.id ?? null);
  const allowed = isReplicationRoleAllowed(
    EPOCUS_BILLING_STATUS_PULL_ROLES,
    role,
  );

  const [checkpointEntity, setCheckpointEntity] = useState<string | null>(null);
  useEffect(() => {
    const programs = db?.[RXDB_EPOCUS_PROGRAM_COLLECTION_NAME];
    if (!programs) return;
    const subscription = programs
      .find()
      .$.subscribe((docs) =>
        setCheckpointEntity(programSetKey(docs.map((doc) => doc.id))),
      );
    return () => subscription.unsubscribe();
  }, [db]);

  const pull = useMemo<
    ReplicationPullConfig<WholeSyncableEpocusBillingStatusFragment> | undefined
  >(
    () =>
      checkpointEntity === null
        ? undefined
        : {
            query: SyncEpocusBillingStatusPullDocument,
            reSyncInterval: { minutes: 5 },
            ...createPersistedCheckpointPullBuilders<
              QueryPullEpocusBillingStatusArgs,
              WholeSyncableEpocusBillingStatusFragment
            >({
              buildInputVariables: (checkpoint, batchSize) => ({
                input: {
                  limit: batchSize,
                  checkpoint,
                  filters: performerId ? { performerId } : null,
                },
              }),
              buildPullCheckpoint: (doc) => ({
                id: doc.id,
                updatedAt: doc.meta.updatedAt,
              }),
              entityId: performerId
                ? `${checkpointEntity}-mine`
                : checkpointEntity,
              db,
              collectionName: collection?.name,
            }),
          },
    [db, collection, checkpointEntity, performerId],
  );

  return useGqlReplication<
    EpocusBillingStatus,
    WholeSyncableEpocusBillingStatusFragment,
    null
  >({
    db,
    collection,
    pull: allowed ? pull : undefined,
    mapGqlToRx: mapGqlEpocusBillingStatusToRx,
    batchSize: 200,
    expireData: false,
    metadataLocalStorageKeySuffix: "epocus-billing-status",
    enabled: allowed,
  });
};

export const mapGqlEpocusBillingStatusToRx = (
  doc: WholeSyncableEpocusBillingStatusFragment,
): WithDeleted<EpocusBillingStatus> => ({
  id: doc.id,
  examId: doc.examId,
  encounterId: doc.encounterId,
  programId: doc.programId ?? null,
  performerId: doc.performerId ?? null,
  status: doc.status as BillingState,
  missingItems: doc.missingItems.map((item) => item as BillingMissingItem),
  holdReason: (doc.holdReason ?? null) as BillingHoldReason | null,
  qaAfterExport: (doc.qaAfterExport ?? null) as BillingHoldReason | null,
  amendedAfterExportAt: doc.amendedAfterExportAt ?? null,
  computedAt: doc.computedAt,
  examType: doc.examType ?? null,
  examSignedAt: doc.examSignedAt ?? null,
  examCreatedAt: doc.examCreatedAt ?? null,
  meta: {
    createdAt: doc.meta.createdAt,
    updatedAt: doc.meta.updatedAt,
    createdByUserId: doc.meta.createdByUserId,
    updatedByUserId: doc.meta.updatedByUserId,
  },
  _deleted: doc.meta.deleted,
});

gql`
  fragment WholeSyncableEpocusBillingStatus on SyncableEpocusBillingStatus {
    id
    examId
    encounterId
    programId
    performerId
    status
    missingItems
    holdReason
    qaAfterExport
    amendedAfterExportAt
    computedAt
    examType
    examSignedAt
    examCreatedAt
    meta {
      createdAt
      updatedAt
      createdByUserId
      updatedByUserId
      deleted
    }
  }
`;

gql`
  query SyncEpocusBillingStatusPull($input: PullEpocusBillingStatusInput!) {
    pullEpocusBillingStatus(input: $input) {
      checkpoint {
        id
        updatedAt
      }
      documents {
        ...WholeSyncableEpocusBillingStatus
      }
    }
  }
`;
